import { FREE_MONTH_PROMO } from 'constants/freeMonthPromo';

/**
 * Free-month promo helper.
 * Throttles the SIMPLIFIED promo popup via localStorage (logged-in or anonymous).
 */

const {
	STORAGE_KEY,
	ANONYMOUS_KEY,
	ONE_DAY_MS,
	DISMISS_COOLDOWN_MS,
} = FREE_MONTH_PROMO;

/**
 * Normalize a tracking key for logged-in users or anonymous visitors.
 * @param {string|number|null|undefined} userId
 * @returns {string}
 */
export function getTrackingKey(userId) {
	return userId != null && userId !== '' ? String(userId) : ANONYMOUS_KEY;
}

/**
 * Read tracking data for a tracking key.
 * @param {string} trackingKey
 * @returns {Object}
 */
const getTracking = (trackingKey) => {
	if (!trackingKey) return {};

	try {
		const allTracking = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
		return allTracking[trackingKey] || {};
	} catch (error) {
		console.error('Error reading free month promo tracking:', error);
		return {};
	}
};

/**
 * Persist tracking data for a tracking key.
 * @param {string} trackingKey
 * @param {Object} data
 */
const saveTracking = (trackingKey, data) => {
	if (!trackingKey) return;

	try {
		const allTracking = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
		allTracking[trackingKey] = { ...allTracking[trackingKey], ...data };
		localStorage.setItem(STORAGE_KEY, JSON.stringify(allTracking));
	} catch (error) {
		console.error('Error saving free month promo tracking:', error);
	}
};

/**
 * Whether the free-month promo popup should show.
 * @param {string|number|null|undefined} userId
 * @param {Object} [options]
 * @param {boolean} [options.force]
 * @returns {boolean}
 */
export function shouldShowFreeMonthPromo(userId, options = {}) {
	if (options?.force) return true;

	const trackingKey = getTrackingKey(userId);
	const tracking = getTracking(trackingKey);
	const now = Date.now();

	if (tracking.lastShown && now - tracking.lastShown < ONE_DAY_MS) {
		return false;
	}

	if (
		tracking.lastDismissed &&
		now - tracking.lastDismissed < DISMISS_COOLDOWN_MS
	) {
		return false;
	}

	return true;
}

/**
 * Record that the promo popup was shown.
 * @param {string|number|null|undefined} userId
 */
export function markFreeMonthPromoShown(userId) {
	const trackingKey = getTrackingKey(userId);
	const tracking = getTracking(trackingKey);

	saveTracking(trackingKey, {
		lastShown: Date.now(),
		showCount: (tracking.showCount || 0) + 1,
	});
}

/**
 * Record that the promo popup was dismissed.
 * @param {string|number|null|undefined} userId
 */
export function markFreeMonthPromoDismissed(userId) {
	const trackingKey = getTrackingKey(userId);
	const tracking = getTracking(trackingKey);

	saveTracking(trackingKey, {
		lastDismissed: Date.now(),
		dismissCount: (tracking.dismissCount || 0) + 1,
	});
}

/**
 * Clear tracking for tests.
 * @param {string|number|null|undefined} [userId]
 */
export function clearFreeMonthPromoTracking(userId) {
	try {
		if (userId === undefined) {
			localStorage.removeItem(STORAGE_KEY);
			return;
		}

		const trackingKey = getTrackingKey(userId);
		const allTracking = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
		delete allTracking[trackingKey];
		localStorage.setItem(STORAGE_KEY, JSON.stringify(allTracking));
	} catch (error) {
		console.error('Error clearing free month promo tracking:', error);
	}
}
